import { Dispatch, SetStateAction } from 'react';
import { API } from 'apis';
import { getViaAuth, postViaAuth, responseValidator } from 'apis/api';
import {
    CallTask,
    CampaignMember,
    CampaignReport,
    CreateMarketingActivityBody,
    GoldUpgradeCandidate,
    GoldUpgradeCandidateDetails,
    MarketingActivity,
    MarketingCampaign,
    PaginationList,
    RunUserReturnCampaignResult,
    UpdateMarketingActivityBody,
    UpdateMarketingCampaignBody,
} from 'core/domain';

type SetLoading = Dispatch<SetStateAction<boolean>>;

const unwrap = <T>(response: any): T | null =>
    responseValidator(response?.status) && response.value !== undefined ? (response.value as T) : null;

// امضای getterهای صفحه‌بندی مطابق انتظار کامپوننت Table/Pagination است.
export const getMarketingActivities = async (
    setLoading: SetLoading,
    setData: Dispatch<SetStateAction<PaginationList<MarketingActivity> | undefined>>,
    page: number,
    pageSize: number,
    query?: string,
) => {
    setLoading(true);
    const response: any = await getViaAuth<PaginationList<MarketingActivity>>(
        `${API.marketing.activities}/${page}/${pageSize}${query || ''}`,
    );
    setLoading(false);
    const value = unwrap<PaginationList<MarketingActivity>>(response);
    if (value) setData(value);
};

export const createMarketingActivity = async (body: CreateMarketingActivityBody) =>
    unwrap<{ id: number }>(await postViaAuth(API.marketing.createActivity, body));

export const updateMarketingActivity = async (body: UpdateMarketingActivityBody) =>
    unwrap<object>(await postViaAuth(API.marketing.updateActivity, body)) !== null;

export const getCallTasks = async (
    setLoading: SetLoading,
    setData: Dispatch<SetStateAction<PaginationList<CallTask> | undefined>>,
    page: number,
    pageSize: number,
    query?: string,
) => {
    setLoading(true);
    const response: any = await getViaAuth<PaginationList<CallTask>>(
        `${API.marketing.callTasks}/${page}/${pageSize}${query || ''}`,
    );
    setLoading(false);
    const value = unwrap<PaginationList<CallTask>>(response);
    if (value) setData(value);
};

export const startCallTask = async (userId: string) =>
    unwrap<{ activityId: number }>(await postViaAuth(API.marketing.startCallTask, { userId }));

export const getMarketingCampaigns = async (setLoading: SetLoading) => {
    setLoading(true);
    const response: any = await getViaAuth<MarketingCampaign[]>(API.marketing.campaigns);
    setLoading(false);
    return unwrap<MarketingCampaign[]>(response);
};

export const updateMarketingCampaign = async (body: UpdateMarketingCampaignBody) =>
    unwrap<object>(await postViaAuth(API.marketing.updateCampaign, body)) !== null;

export const runUserReturnCampaign = async () =>
    unwrap<RunUserReturnCampaignResult>(await postViaAuth(API.marketing.runUserReturnCampaign, {}));

export const getCampaignReport = async (setLoading: SetLoading, campaignId: number, query?: string) => {
    setLoading(true);
    const response: any = await getViaAuth<CampaignReport>(
        `${API.marketing.campaignReport}/${campaignId}${query || ''}`,
    );
    setLoading(false);
    return unwrap<CampaignReport>(response);
};

/** getter صفحه‌بندی اعضای یک کمپین؛ شناسه‌ی کمپین را از بیرون می‌گیرد تا امضا با Table یکی بماند. */
export const campaignMembersGetter =
    (campaignId: number) =>
    async (
        setLoading: SetLoading,
        setData: Dispatch<SetStateAction<PaginationList<CampaignMember> | undefined>>,
        page: number,
        pageSize: number,
        query?: string,
    ) => {
        setLoading(true);
        const response: any = await getViaAuth<PaginationList<CampaignMember>>(
            `${API.marketing.campaignMembers}/${campaignId}/${page}/${pageSize}${query || ''}`,
        );
        setLoading(false);
        const value = unwrap<PaginationList<CampaignMember>>(response);
        if (value) setData(value);
    };

export const getGoldUpgradeCandidates = async (
    setLoading: SetLoading,
    setData: Dispatch<SetStateAction<PaginationList<GoldUpgradeCandidate> | undefined>>,
    page: number,
    pageSize: number,
    query?: string,
) => {
    setLoading(true);
    const response: any = await getViaAuth<PaginationList<GoldUpgradeCandidate>>(
        `${API.marketing.goldUpgradeCandidates}/${page}/${pageSize}${query || ''}`,
    );
    setLoading(false);
    const value = unwrap<PaginationList<GoldUpgradeCandidate>>(response);
    setData(value ?? undefined);
};

export const getGoldUpgradeCandidateDetails = async (userId: string) =>
    unwrap<GoldUpgradeCandidateDetails>(
        await getViaAuth<GoldUpgradeCandidateDetails>(`${API.marketing.goldUpgradeCandidateDetails}/${userId}`),
    );

export const upgradeUserToGold = async (userId: string) =>
    unwrap<{ smsSent: boolean }>(await postViaAuth(API.marketing.upgradeToGold, { userId }));
